import { Segmented } from 'antd';
import { useState, useRef, useEffect } from 'react';
import { Area } from '@ant-design/plots';
import PropTypes from 'prop-types';
import { ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { AiOutlineCar } from 'react-icons/ai';
import { useTranslation } from 'react-i18next';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return `${date.getDate().toString().padStart(2, '0')}`;
};

const SessionsGraph = ({ data, currentMonth, previousMonth, summary }) => {
  const { t } = useTranslation();
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [chartHeight, setChartHeight] = useState(200);
  const [chartKey, setChartKey] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setChartHeight(width < 400 ? 180 : 200);
        setChartKey(prev => prev + 1);
      }
    };

    const resizeObserver = new ResizeObserver(updateDimensions);

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    updateDimensions();

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!data || !currentMonth || !previousMonth || !data.data) {
    return null;
  }

  const chartRaw = data.data;
  const isCurrent = selectedMonth === currentMonth;
  const isNegative = summary?.changePercent < 0;
  const currentTotal = summary?.currentTotal ?? 0;
  const previousTotal = summary?.previousTotal ?? 0;
  const selectedMonthTotal = isCurrent ? currentTotal : previousTotal;

  const chartData = chartRaw
    .filter((item) => item[selectedMonth])
    .map((item) => ({
      date: formatDate(item.date),
      value: item[selectedMonth],
      fullDate: item.date
    }));

  // Calculate optimal tick count based on data length
  const tickCount = Math.min(6, chartData.length);

  return (
    <div className="bg-white rounded-xl border border-gray-200 transition duration-200 p-4 h-full flex flex-col space-y-3" ref={containerRef}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <AiOutlineCar className="text-2xl text-indigo-500" />
          <div>
            <div className="font-semibold  text-sm">{t('dashboard:sessionsTitle')}</div>
            <div className="text-sm font-bold text-gray-900">{selectedMonthTotal}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-nowrap ml-auto">
          {isCurrent && (
            <>
              <span className={`flex items-center font-semibold text-sm whitespace-nowrap ${isNegative ? 'text-red-600' : 'text-indigo-600'}`}> 
                {isNegative ? <ArrowDownOutlined /> : <ArrowUpOutlined />} 
                {summary && Math.abs(summary.changeAmount)}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold min-w-[40px] text-center whitespace-nowrap ${isNegative ? 'bg-red-100 text-red-600' : 'bg-indigo-100 text-indigo-600'}`}> 
                {isNegative ? '' : '+'}{summary && Math.abs(summary.changePercent)}%
              </span>
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Segmented
          size="small"
          options={[
            { label: t(`dashboard:months.${previousMonth}`, { defaultValue: previousMonth }), value: previousMonth },
            { label: t(`dashboard:months.${currentMonth}`, { defaultValue: currentMonth }), value: currentMonth }
          ]}
          value={selectedMonth}
          onChange={setSelectedMonth}
          className="bg-gray-50"
        />
      </div>

      <div className="flex-grow" style={{ minHeight: chartHeight }}>
        <Area
          key={`${chartKey}-${selectedMonth}`}
          data={chartData}
          xField="date"
          yField="value"
          autoFit={true}
          axis={{
            y: {
              title: t('dashboard:sessions'),
              grid: {
                line: {
                  style: {
                    stroke: '#f0f0f0',
                    lineWidth: 1,
                    lineDash: [4, 4],
                  },
                },
              },
              label: {
                formatter: (val) => Math.round(val),
                style: {
                  fontSize: 11,
                },
                offset: 10,
              },
            },
            x: {
              label: {
                style: {
                  fontSize: 10,
                  angle: 45,
                },
                offset: 25,
              },
              tickCount: tickCount,
              grid: null,
            },
          }}
          animate={{
            enter: {
              type: 'waveIn',
              duration: 600,
            },
          }}
          tooltip={{
            title: t('dashboard:tooltip.sessions'),
            items: [{ 
              channel: 'y', 
              name: t('dashboard:tooltip.sessions'),
              valueFormatter: (val) => `${Math.round(val)} ${t('dashboard:sessions').toLowerCase()}` 
            }],
          }}
          inset={10}
          style={{
            fill: selectedMonth === currentMonth
              ? 'l(90) 0:#e0e7ff 1:#4f46e5'
              : 'l(90) 0:#ffffff 1:#eedfc4',
          }}
          areaStyle={{
            fillOpacity: 0.2,
          }}
          line={{
            style: {
              stroke: selectedMonth === currentMonth ? '#4f46e5' : '#d97706',
              strokeWidth: 2,
            },
          }}
          height={chartHeight}
          legend={false}
        />
      </div>
    </div>
  );
};

SessionsGraph.propTypes = {
  data: PropTypes.object,
  currentMonth: PropTypes.string,
  previousMonth: PropTypes.string,
  summary: PropTypes.object,
};

export default SessionsGraph; 